

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./Companies.css";
import { resolveLogoSrc } from "../../utils/logoUtils";

/* ── API Config ─────────────────────────────────────────── */
function readStoredToken() {
    return (
        localStorage.getItem("th_token") ||
        localStorage.getItem("token") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("th_employer_token") ||
        ""
    );
}

async function authFetch(path, options = {}) {
    const token = readStoredToken();
    const url = path.startsWith("http")
        ? path
        : path.startsWith("/api")
            ? path
            : `/api${path.startsWith("/") ? path : `/${path}`}`;
    const res = await fetch(url, {
        credentials: "include",
        ...options,
        headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(options.headers || {}),
        },
    });
    if (!res.ok) throw new Error(`API ${res.status}: ${res.statusText}`);
    return res.json();
}

const INDUSTRIES = ["All Industries", "Technology", "IT Services", "Telecom", "E-Commerce", "FoodTech", "Fintech", "Manufacturing", "BFSI", "Healthcare", "Education"];
const SORT_OPTIONS = [
    { value: "rating", label: "Highest Rated" },
    { value: "reviews", label: "Most Reviewed" },
    { value: "jobs", label: "Most Jobs" },
    { value: "name", label: "A – Z" },
];

/* ── Helpers ── */
function fmtNum(n) {
    if (!n && n !== 0) return "—";
    if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
    if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "k";
    return n.toString();
}

function RatingStars({ rating }) {
    const full = Math.floor(rating);
    const half = rating % 1 >= 0.5;
    const empty = 5 - full - (half ? 1 : 0);
    const color = rating >= 4.0 ? "#263012" : rating >= 3.5 ? "#263012" : "#394420";
    return (
        <div className="cc-stars" style={{ "--star-color": color }}>
            {"★".repeat(full)}
            {half ? "⯨" : ""}
            {"☆".repeat(empty)}
        </div>
    );
}

function RatingBadge({ rating, color }) {
    return (
        <div className="cc-rating-badge" style={{ background: color }}>
            <span className="cc-rating-badge__star">★</span>
            <span className="cc-rating-badge__val">{rating.toFixed(1)}</span>
        </div>
    );
}

function ratingColor(r) {
    if (r >= 4.0) return "#263012";
    if (r >= 3.5) return "#263012";
    return "#394420";
}

const LIVE_SORT_OPTIONS = [
    { value: "jobs", label: "Most Hiring" },
    { value: "applicants", label: "Most Applied" },
    { value: "recent", label: "Recently Active" },
    { value: "name", label: "A - Z" },
];

const COMPANY_TABS = [
    { key: "about", label: "About" },
    { key: "reviews", label: "Reviews" },
    { key: "salaries", label: "Salaries" },
    { key: "interviews", label: "Interviews" },
    { key: "jobs", label: "Jobs" },
    { key: "benefits", label: "Benefits" },
    { key: "photos", label: "Photos" },
    { key: "posts", label: "Posts" },
    { key: "news", label: "News" },
    { key: "offices", label: "Offices" },
    { key: "faq", label: "FAQ" },
];

function mapApiCompany(company = {}) {
    const name =
        company.company_name ||
        company.name ||
        company.company ||
        "Unknown Company";
    const initials = (name || "C")
        .split(" ")
        .map(part => part.trim()[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase() || "C";

    return {
        id: company.id,
        name,
        logo: resolveLogoSrc(company.company_logo || company.logo || ""),
        logoFallbackLetter: initials,
        logoFallbackBg: "#263012",
        industry: company.industry || "Hiring",
        location: company.location || company.city || "India",
        primaryLocation: company.location || company.city || "India",
        otherLocationsCount: Number(company.other_locations_count || 0),
        activeJobs: Number(company.active_jobs || 0),
        totalJobs: Number(company.total_jobs || company.active_jobs || 0),
        applicants: Number(company.total_applicants || 0),
        companySize: company.company_size || company.size || "Not shared",
        website: company.website || "",
        about: company.about || company.hiring_bio || "",
        latestJobAt: company.latest_job_at || null,
        rating: Number(company.rating || 4.5),
        ratingCount: Number(company.rating_count || company.ratingCount || Math.max(Number(company.total_applicants || 0), 150)),
        criticallyRatedFor: Array.isArray(company.criticallyRatedFor)
            ? company.criticallyRatedFor
            : ["Work Life Balance", "Skill Development", "Company Culture"],
        designations: Array.isArray(company.designations) && company.designations.length
            ? company.designations
            : ["Software Engineer", "Frontend Developer", "Backend Developer", "Product Manager", "Designer"],
        stats: company.stats || {
            reviews: Math.max(Number(company.total_applicants || 0), Number(company.active_jobs || 0) * 12),
            salaries: Math.max(Number(company.active_jobs || 0) * 4, 1),
            interviews: Math.max(Number(company.active_jobs || 0) * 2, 1),
            jobs: Number(company.active_jobs || 0),
            benefits: Math.max(Number(company.active_jobs || 0), 1),
            photos: Math.max(Math.round(Number(company.active_jobs || 0) / 2), 1),
            posts: Math.max(Math.round(Number(company.active_jobs || 0) / 3), 1),
            news: Math.max(Math.round(Number(company.active_jobs || 0) / 4), 1),
            offices: Number(company.location ? 1 : 0),
            faq: 1,
        },
        isFollowed: false,
    };
}

/* ── Company Card ── */
function LiveCompanyCard({ company, delay = 0 }) {
    const navigate = useNavigate();

    const openCompany = (tab = "about") => {
        navigate(`/employee/company/${company.id}?tab=${tab}`);
    };
    return (
        <div className="cc-card" style={{ animationDelay: `${delay}ms` }}>
            <div className="cc-card__top">
                <div className="cc-logo-wrap">
                    {company.logo ? (
                        <img
                            src={company.logo}
                            alt={company.name}
                            className="cc-logo-img"
                            onError={e => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                        />
                    ) : null}
                    <div
                        className="cc-logo-fallback"
                        style={{
                            background: company.logoFallbackBg,
                            display: company.logo ? "none" : "flex",
                        }}
                    >
                        {company.logoFallbackLetter}
                    </div>
                </div>

                <div className="cc-card__identity">
                    <div className="cc-card__name-row">
                        <h3 className="cc-card__name">{company.name}</h3>
                        <span className="cc-follow-btn cc-follow-btn--active" style={{ cursor: "default" }}>
                            Hiring
                        </span>
                    </div>

                    <div className="cc-meta-row" style={{ marginBottom: 8 }}>
                        <span className="cc-industry">{company.industry}</span>
                        <span className="cc-meta-dot">Â·</span>
                        <span className="cc-location">ðŸ“ {company.location}</span>
                    </div>

                    <div className="cc-meta-row">
                        <span className="cc-location">
                            {company.about || "Actively hiring on Talent Hive"}
                        </span>
                    </div>
                </div>
            </div>

            <div className="cc-divider" />

            <div
                className="cc-company-tabs"
                style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    marginBottom: 14,
                }}
            >
                {COMPANY_TABS.map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        className="cc-company-tab"
                        onClick={() => openCompany(tab.key)}
                        style={{
                            border: "1px solid rgba(18,22,10,.18)",
                            background: "#f2f5ea",
                            color: "#c2410c",
                            borderRadius: 999,
                            padding: "8px 12px",
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: "pointer",
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="cc-stats-row" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
                <div className="cc-stat">
                    <span className="cc-stat__val">{fmtNum(company.activeJobs)}</span>
                    <span className="cc-stat__lbl">Active Jobs</span>
                </div>
                <div className="cc-stat">
                    <span className="cc-stat__val">{fmtNum(company.totalJobs)}</span>
                    <span className="cc-stat__lbl">Total Jobs</span>
                </div>
                <div className="cc-stat">
                    <span className="cc-stat__val">{fmtNum(company.applicants)}</span>
                    <span className="cc-stat__lbl">Applicants</span>
                </div>
                <div className="cc-stat">
                    <span className="cc-stat__val">{company.companySize}</span>
                    <span className="cc-stat__lbl">Company Size</span>
                </div>
            </div>

            <div className="cc-divider" />

            <div className="cc-card__footer" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                    className="cc-empty__btn"
                    style={{ flex: 1, minWidth: 140 }}
                    onClick={() => openCompany("jobs")}
                >
                    View Jobs
                </button>
                {company.website && (
                    <a
                        className="cc-empty__btn"
                        style={{ flex: 1, minWidth: 140, textDecoration: "none", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                        href={company.website.startsWith("http") ? company.website : `https://${company.website}`}
                        target="_blank"
                        rel="noreferrer"
                    >
                        Website
                    </a>
                )}
            </div>
        </div>
    );
}

function LiveCompanyCardModern({ company, delay = 0 }) {
    const navigate = useNavigate();

    const openCompany = (tab = "about") => {
        navigate(`/employee/company/${company.id}?tab=${tab}`);
    };

    const rating = Number(company.rating || 4.5);
    const ratingCount = Number(company.ratingCount || 150);
    const ratingColorValue = ratingColor(rating);
    const highlights = company.criticallyRatedFor && company.criticallyRatedFor.length
        ? company.criticallyRatedFor
        : ["Work Life Balance", "Skill Development", "Company Culture"];
    const quickStats = [
        { key: "reviews", label: "Reviews", value: company.stats?.reviews ?? 0 },
        { key: "salaries", label: "Salaries", value: company.stats?.salaries ?? 0 },
        { key: "interviews", label: "Interviews", value: company.stats?.interviews ?? 0 },
        { key: "jobs", label: "Jobs", value: company.stats?.jobs ?? company.activeJobs ?? 0 },
        { key: "benefits", label: "Benefits", value: company.stats?.benefits ?? 0 },
        { key: "photos", label: "Photos", value: company.stats?.photos ?? 0 },
    ];
    return (
        <div
            className="cc-card cc-card--company"
            style={{ animationDelay: `${delay}ms`, cursor: "pointer" }}
            onClick={() => openCompany("about")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") openCompany("about");
            }}
        >
            <div className="cc-card__top">
                <div className="cc-logo-wrap">
                    {company.logo ? (
                        <img
                            src={company.logo}
                            alt={company.name}
                            className="cc-logo-img"
                            onError={e => {
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "flex";
                            }}
                        />
                    ) : null}
                    <div
                        className="cc-logo-fallback"
                        style={{
                            background: company.logoFallbackBg,
                            display: company.logo ? "none" : "flex",
                        }}
                    >
                        {company.logoFallbackLetter}
                    </div>
                </div>

                <div className="cc-card__identity">
                    <div className="cc-card__name-row">
                        <h3 className="cc-card__name">{company.name}</h3>
                        <span className="cc-company-follow">Follow</span>
                    </div>

                    <div className="cc-rating-row">
                        <RatingBadge rating={rating} color={ratingColorValue} />
                        <RatingStars rating={rating} />
                        <span className="cc-rating-count">({fmtNum(ratingCount)})</span>
                    </div>

                    <div className="cc-meta-row">
                        <span className="cc-location">
                            {company.primaryLocation || company.location || "India"}
                            {company.otherLocationsCount > 0 && (
                                <span className="cc-other-loc"> +{company.otherLocationsCount} other locations</span>
                            )}
                        </span>
                    </div>
                </div>
            </div>

            <div className="cc-divider" />

            <div className="cc-highlights">
                <div className="cc-highlights__label">Best known for</div>
                <div className="cc-highlights__tags">
                    {highlights.map((item) => (
                        <span key={item} className="cc-highlights__tag">{item}</span>
                    ))}
                </div>
            </div>

            <div className="cc-quick-head">
                <span className="cc-quick-head__title">Explore the profile</span>
                <span className="cc-quick-head__hint">Use the tabs below to jump to reviews, jobs, salaries, and more.</span>
            </div>

            <div className="cc-stats-row cc-stats-row--company">
                {quickStats.map((stat) => (
                    <button
                        key={stat.key}
                        type="button"
                        className="cc-stat cc-stat--link"
                        onClick={() => openCompany(stat.key)}
                    >
                        <span className="cc-stat__val">{fmtNum(stat.value)}</span>
                        <span className="cc-stat__lbl">{stat.label}</span>
                    </button>
                ))}
            </div>

            <div className="cc-more-links">
                {COMPANY_TABS.map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        className="cc-more-link"
                        onClick={() => openCompany(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

function CompanyCard({ company, onFollow, delay = 0 }) {
    const [followed, setFollowed] = useState(company.isFollowed);
    const [followLoading, setFollowLoading] = useState(false);
    const color = ratingColor(company.rating);

    async function handleFollow(e) {
        e.stopPropagation();
        setFollowLoading(true);
        try {
            await authFetch(`/api/companies/${company.id}/follow`, { method: "POST" });
            setFollowed(f => !f);
            onFollow && onFollow(company.id, !followed);
        } catch {
            setFollowed(f => !f); // optimistic even if API fails
        } finally {
            setFollowLoading(false);
        }
    }

    const stats = [
        { label: "Reviews", val: company.stats.reviews },
        { label: "Salaries", val: company.stats.salaries },
        { label: "Interviews", val: company.stats.interviews },
        { label: "Jobs", val: company.stats.jobs },
        { label: "Benefits", val: company.stats.benefits },
        { label: "Photos", val: company.stats.photos },
    ];

    return (
        <div className="cc-card" style={{ animationDelay: `${delay}ms` }}>

            {/* Top section */}
            <div className="cc-card__top">
                {/* Logo */}
                <div className="cc-logo-wrap">
                    {company.logo ? (
                        <img src={company.logo} alt={company.name} className="cc-logo-img"
                            onError={e => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
                    ) : null}
                    <div className="cc-logo-fallback"
                        style={{
                            background: company.logoFallbackBg,
                            display: company.logo ? "none" : "flex",
                        }}>
                        {company.logoFallbackLetter}
                    </div>
                </div>

                {/* Name + Follow */}
                <div className="cc-card__identity">
                    <div className="cc-card__name-row">
                        <h3 className="cc-card__name">{company.name}</h3>
                        <button
                            className={`cc-follow-btn${followed ? " cc-follow-btn--active" : ""}`}
                            onClick={handleFollow}
                            disabled={followLoading}>
                            {followLoading ? "…" : followed ? "✓ Following" : "+ Follow"}
                        </button>
                    </div>

                    {/* Rating row */}
                    <div className="cc-rating-row">
                        <RatingBadge rating={company.rating} color={color} />
                        <RatingStars rating={company.rating} />
                        <span className="cc-rating-count">({fmtNum(company.ratingCount)})</span>
                    </div>

                    {/* Industry + Location */}
                    <div className="cc-meta-row">
                        <span className="cc-industry">{company.industry}</span>
                        <span className="cc-meta-dot">·</span>
                        <span className="cc-location">
                            📍 {company.primaryLocation}
                            {company.otherLocationsCount > 0 && (
                                <span className="cc-other-loc"> +{company.otherLocationsCount} other locations</span>
                            )}
                        </span>
                    </div>
                </div>
            </div>

            {/* Divider */}
            <div className="cc-divider" />

            {/* Critically rated */}
            {company.criticallyRatedFor && company.criticallyRatedFor.length > 0 && (
                <div className="cc-critical">
                    <div className="cc-critical__icon">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3H10z" fill="#394420" opacity=".15" />
                            <path d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3H10z" stroke="#394420" strokeWidth="1.5" fill="none" />
                            <path d="M17 2h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17" stroke="#394420" strokeWidth="1.5" />
                        </svg>
                    </div>
                    <div className="cc-critical__text">
                        <span className="cc-critical__label">Critically Rated For</span>
                        <div className="cc-critical__tags">
                            {company.criticallyRatedFor.map((t, i) => (
                                <span key={i} className="cc-critical__tag">{t}</span>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className="cc-divider" />

            {/* Stats row */}
            <div className="cc-stats-row">
                {stats.map((s, i) => (
                    <div key={i} className="cc-stat">
                        <span className="cc-stat__val">{fmtNum(s.val)}</span>
                        <span className="cc-stat__lbl">{s.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ── Skeleton Card ── */
function SkeletonCard() {
    return (
        <div className="cc-card cc-card--skeleton">
            <div className="cc-card__top">
                <div className="cc-skel cc-skel--logo" />
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                    <div className="cc-skel" style={{ width: "60%", height: 18 }} />
                    <div className="cc-skel" style={{ width: "40%", height: 14 }} />
                    <div className="cc-skel" style={{ width: "70%", height: 12 }} />
                </div>
            </div>
            <div className="cc-divider" />
            <div className="cc-skel" style={{ width: "50%", height: 12, margin: "12px 0" }} />
            <div className="cc-skel" style={{ width: "100%", height: 36, borderRadius: 8 }} />
            <div className="cc-divider" style={{ marginTop: 12 }} />
            <div style={{ display: "flex", gap: 10 }}>
                {[1, 2, 3, 4, 5, 6].map(i => (
                    <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
                        <div className="cc-skel" style={{ width: "70%", height: 14 }} />
                        <div className="cc-skel" style={{ width: "80%", height: 10 }} />
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ── Filter Bar ── */
function FilterBar({ search, onSearch, industry, onIndustry, sort, onSort, total }) {
    return (
        <div className="cc-filters">
            {/* Search */}
            <div className="cc-search-box">
                <svg className="cc-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                <input
                    className="cc-search-input"
                    type="text"
                    placeholder="Search companies…"
                    value={search}
                    onChange={e => onSearch(e.target.value)}
                />
                {search && (
                    <button className="cc-search-clear" onClick={() => onSearch("")}>✕</button>
                )}
            </div>

            {/* Industry filter */}
            <select className="cc-filter-select" value={industry} onChange={e => onIndustry(e.target.value)}>
                {INDUSTRIES.map(ind => (
                    <option key={ind} value={ind}>{ind}</option>
                ))}
            </select>

            {/* Sort */}
            <select className="cc-filter-select" value={sort} onChange={e => onSort(e.target.value)}>
                {LIVE_SORT_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>

            <span className="cc-results-count">{total.toLocaleString()} companies</span>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════ */
export default function CompanyCards() {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [industry, setIndustry] = useState("All Industries");
    const [sort, setSort] = useState("jobs");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState({ companies: 0, activeJobs: 0, applicants: 0 });
    const debRef = useRef(null);
    const LIMIT = 12;

    const fetchCompanies = useCallback(async (pg = 1, q = search, ind = industry, srt = sort) => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                page: pg,
                limit: LIMIT,
                search: q,
                industry: ind === "All Industries" ? "" : ind,
                sort: srt,
            });
            const data = await authFetch(`/api/companies?${params}`);
            const rows = Array.isArray(data.data) ? data.data.map(mapApiCompany) : [];
            setCompanies(rows);
            setTotal(data.total || rows.length);
            setTotalPages(data.totalPages || Math.max(1, Math.ceil((data.total || rows.length) / LIMIT)));
            setSummary({
                companies: data.total || rows.length,
                activeJobs: data.summary?.activeJobs ?? rows.reduce((sum, item) => sum + Number(item.activeJobs || 0), 0),
                applicants: data.summary?.applicants ?? rows.reduce((sum, item) => sum + Number(item.applicants || 0), 0),
            });
            setPage(pg);
        } catch (err) {
            console.error("Failed to load companies from database:", err.message);
            setCompanies([]);
            setTotal(0);
            setTotalPages(1);
            setSummary({ companies: 0, activeJobs: 0, applicants: 0 });
            setPage(1);
            setError("Could not load companies from the database.");
        } finally {
            setLoading(false);
        }
    }, [search, industry, sort]);

    // Debounce search
    useEffect(() => {
        if (debRef.current) clearTimeout(debRef.current);
        debRef.current = setTimeout(() => {
            fetchCompanies(1, search, industry, sort);
        }, 320);
        return () => clearTimeout(debRef.current);
    }, [search, industry, sort]);

    function handleFollow(id, followed) {
        setCompanies(prev =>
            prev.map(c => c.id === id ? { ...c, isFollowed: followed } : c)
        );
    }

    const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

    return (
        <div className="cc-root">
            {/* Header */}
            <div className="cc-page-header">
                <div className="cc-page-header__inner">
                    <div className="cc-page-header__text">
                        <div className="cc-page-eyebrow">Employer Directory</div>
                        <h1 className="cc-page-title">
                            Find Hiring <span className="cc-page-title--accent">Companies</span>
                        </h1>
                        <p className="cc-page-subtitle">
                            Live companies from your portal, sorted by active openings and recruiter activity.
                        </p>
                    </div>
                    <div className="cc-page-header__stats">
                        <div className="cc-header-stat">
                            <span className="cc-header-stat__val">{fmtNum(summary.companies)}</span>
                            <span className="cc-header-stat__lbl">Companies</span>
                        </div>
                        <div className="cc-header-stat">
                            <span className="cc-header-stat__val">{fmtNum(summary.activeJobs)}</span>
                            <span className="cc-header-stat__lbl">Open Roles</span>
                        </div>
                        <div className="cc-header-stat">
                            <span className="cc-header-stat__val">{fmtNum(summary.applicants)}</span>
                            <span className="cc-header-stat__lbl">Applicants</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="cc-container">
                {/* Filter bar */}
                <FilterBar
                    search={search} onSearch={setSearch}
                    industry={industry} onIndustry={setIndustry}
                    sort={sort} onSort={setSort}
                    total={total}
                />

                {/* Error state */}
                {error && (
                    <div className="cc-error">
                        <span>⚠️</span> {error}
                        <button onClick={() => fetchCompanies(1)}>Retry</button>
                    </div>
                )}

                {/* Grid */}
                <div className="cc-grid">
                    {loading
                        ? Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)
                        : companies.length === 0
                            ? (
                                <div className="cc-empty">
                                    <div className="cc-empty__icon">🏢</div>
                                    <div className="cc-empty__title">No companies found</div>
                                    <div className="cc-empty__sub">Try adjusting your search or filters.</div>
                                    <button className="cc-empty__btn" onClick={() => { setSearch(""); setIndustry("All Industries"); }}>
                                        Clear Filters
                                    </button>
                                </div>
                            )
                            : companies.map((co, i) => (
                                <LiveCompanyCardModern
                                    key={co.id}
                                    company={co}
                                    delay={i * 55}
                                />
                            ))
                    }
                </div>

                {/* Pagination */}
                {!loading && totalPages > 1 && (
                    <div className="cc-pagination">
                        <button
                            className="cc-page-btn"
                            disabled={page <= 1}
                            onClick={() => fetchCompanies(page - 1)}>
                            ← Prev
                        </button>
                        <div className="cc-page-nums">
                            {pages.map(p => (
                                <button key={p}
                                    className={`cc-page-num${p === page ? " cc-page-num--active" : ""}`}
                                    onClick={() => fetchCompanies(p)}>
                                    {p}
                                </button>
                            ))}
                        </div>
                        <button
                            className="cc-page-btn"
                            disabled={page >= totalPages}
                            onClick={() => fetchCompanies(page + 1)}>
                            Next →
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

